import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { SrIcon } from "@/components/ui/SrIcon";

export function ChatInput({
  onSend,
  onTypingChange,
  disabled,
}: {
  onSend: (body: string, file: File | null) => Promise<void>;
  onTypingChange?: (typing: boolean) => void;
  disabled?: boolean;
}) {
  const [body, setBody] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const canSend = !sending && (body.trim().length > 0 || file !== null);

  const handleBodyChange = (value: string) => {
    setBody(value);
    if (onTypingChange) onTypingChange(value.trim().length > 0);
  };

  const handleSend = async () => {
    if (!canSend || disabled) return;
    setSending(true);
    try {
      await onSend(body.trim(), file);
      setBody("");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      if (onTypingChange) onTypingChange(false);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="border-t bg-card p-2.5 space-y-1.5">
      {file && (
        <div className="flex items-center gap-2 rounded bg-muted/60 px-2 py-1.5 text-xs">
          <SrIcon name="paperclip" className="w-3.5 h-3.5 text-muted-foreground shrink-0"  />
          <span className="flex-1 min-w-0 truncate">{file.name}</span>
          <button
            type="button"
            onClick={() => {
              setFile(null);
              if (fileRef.current) fileRef.current.value = "";
            }}
            className="text-muted-foreground hover:text-foreground"
          >
            <SrIcon name="x" className="w-3.5 h-3.5"  />
          </button>
        </div>
      )}
      <div className="flex items-end gap-1.5">
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="shrink-0 h-9 w-9"
          onClick={() => fileRef.current?.click()}
          disabled={sending || disabled}
        >
          <SrIcon name="paperclip" className="w-4 h-4"  />
        </Button>
        <textarea
          value={body}
          onChange={(e) => handleBodyChange(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={1}
          placeholder="Skriv en melding..."
          disabled={disabled}
          className={cn(
            "flex-1 min-h-[36px] max-h-32 resize-none rounded-md border bg-background px-3 py-2 text-sm",
            "focus:outline-none focus:ring-1 focus:ring-primary",
          )}
        />
        <Button
          type="button"
          size="icon"
          className="shrink-0 h-9 w-9"
          onClick={handleSend}
          disabled={!canSend || disabled}
        >
          {sending ? <SrIcon name="loader2" className="w-4 h-4 animate-spin"  /> : <SrIcon name="send" className="w-4 h-4"  />}
        </Button>
      </div>
    </div>
  );
}

export default ChatInput;
